import type {
  GeoBrowseDisease,
  GeoBrowseEnrichmentRow,
  GeoBrowseGene,
  GeoBrowseGeneStudy,
  GeoBrowseMeta,
  GeoBrowsePcaPoint,
  GeoBrowseRecurrentGene,
  GeoBrowseSample,
  GeoBrowseStudy,
  GeoBrowseStudyDetail,
  GeoBrowseThresholds,
  GeoBrowseVolcanoPoint,
  GeoBrowseDegRow,
} from '../types';

export const MOCK_META: GeoBrowseMeta = {
  dataPath: '/home/janet/Documents/GEO_QUICKWIN/data/browse',
  status: 'ok',
  message: 'data/browse/ found at /home/janet/Documents/GEO_QUICKWIN/data/browse — all tables present.',
  n_studies: 9,
  n_diseases: 8,
};

export const MOCK_DISEASES: GeoBrowseDisease[] = [
  {
    cui: 'C0024141',
    cui_name: 'LUPUS ERYTHEMATOSUS, SYSTEMIC',
    n_studies_total: 98,
    n_studies_usable: 31,
    n_studies_with_deg: 17,
    n_samples_total: 4408,
    n_genes_tested: 37663,
    n_genes_sig: 32752,
  },
  {
    cui: 'C0003873',
    cui_name: 'RHEUMATOID ARTHRITIS',
    n_studies_total: 138,
    n_studies_usable: 16,
    n_studies_with_deg: 8,
    n_samples_total: 4932,
    n_genes_tested: 37663,
    n_genes_sig: 28401,
  },
  {
    cui: 'C0033860',
    cui_name: 'PSORIASIS',
    n_studies_total: 122,
    n_studies_usable: 26,
    n_studies_with_deg: 20,
    n_samples_total: 4680,
    n_genes_tested: 37663,
    n_genes_sig: 24109,
  },
  {
    cui: 'C0026769',
    cui_name: 'MULTIPLE SCLEROSIS',
    n_studies_total: 145,
    n_studies_usable: 30,
    n_studies_with_deg: 17,
    n_samples_total: 5563,
    n_genes_tested: 37663,
    n_genes_sig: 19876,
  },
  {
    cui: 'C0002395',
    cui_name: "ALZHEIMER'S DISEASE",
    n_studies_total: 210,
    n_studies_usable: 42,
    n_studies_with_deg: 28,
    n_samples_total: 6200,
    n_genes_tested: 37663,
    n_genes_sig: 15234,
  },
  {
    cui: 'C0036341',
    cui_name: 'SCHIZOPHRENIA',
    n_studies_total: 89,
    n_studies_usable: 18,
    n_studies_with_deg: 11,
    n_samples_total: 3100,
    n_genes_tested: 37663,
    n_genes_sig: 12450,
  },
  {
    cui: 'C0010674',
    cui_name: 'CYSTIC FIBROSIS',
    n_studies_total: 45,
    n_studies_usable: 12,
    n_studies_with_deg: 7,
    n_samples_total: 890,
    n_genes_tested: 37663,
    n_genes_sig: 8901,
  },
  {
    cui: 'C0021390',
    cui_name: 'INFLAMMATORY BOWEL DISEASES',
    n_studies_total: 76,
    n_studies_usable: 22,
    n_studies_with_deg: 15,
    n_samples_total: 3800,
    n_genes_tested: 37663,
    n_genes_sig: 21500,
  },
];

const MOCK_STUDIES_CORE: Omit<GeoBrowseStudy, 'comparison_id'>[] = [
  {
    cui: 'C0021390',
    cui_name: 'INFLAMMATORY BOWEL DISEASES',
    accession: 'GSE165512',
    title: 'Characterization of the IL23-IL17 immune axis in IBD patients',
    overall_design: 'Mucosal biopsy cohort',
    year: 2021,
    platform: 'rnaseq',
    n_samples: 170,
    n_case: 124,
    n_control: 46,
    usable_for_deg: true,
    has_deg: true,
    n_genes_tested: 37663,
    n_genes_sig: 240,
    qc_flags: 2,
  },
  {
    cui: 'C0002395',
    cui_name: "ALZHEIMER'S DISEASE",
    accession: 'GSE132903',
    title: 'Bulk RNA-seq of AD brain cortex',
    overall_design: '',
    year: 2020,
    platform: 'rnaseq',
    n_samples: 64,
    n_case: 40,
    n_control: 24,
    usable_for_deg: true,
    has_deg: true,
    n_genes_tested: 37663,
    n_genes_sig: 7800,
    qc_flags: 0,
  },
  {
    cui: 'C0033860',
    cui_name: 'PSORIASIS',
    accession: 'GSE74697',
    title: 'Psoriasis skin biopsy transcriptome analysis',
    overall_design: '',
    year: 2015,
    platform: 'rnaseq',
    n_samples: 52,
    n_case: 28,
    n_control: 24,
    usable_for_deg: true,
    has_deg: true,
    n_genes_tested: 37663,
    n_genes_sig: 8178,
    qc_flags: 0,
  },
  {
    cui: 'C0024141',
    cui_name: 'LUPUS ERYTHEMATOSUS, SYSTEMIC',
    accession: 'GSE110999',
    title: 'Transcriptomic profiling of systemic lupus erythematosus peripheral blood',
    overall_design: '',
    year: 2018,
    platform: 'rnaseq',
    n_samples: 48,
    n_case: 24,
    n_control: 24,
    usable_for_deg: true,
    has_deg: true,
    n_genes_tested: 37663,
    n_genes_sig: 30629,
    qc_flags: 0,
  },
  {
    cui: 'C0003873',
    cui_name: 'RHEUMATOID ARTHRITIS',
    accession: 'GSE55457',
    title: 'Synovial tissue RNA-seq in rheumatoid arthritis',
    overall_design: '',
    year: 2014,
    platform: 'microarray',
    n_samples: 36,
    n_case: 18,
    n_control: 18,
    usable_for_deg: true,
    has_deg: true,
    n_genes_tested: 37663,
    n_genes_sig: 9200,
    qc_flags: 3,
  },
  {
    cui: 'C0024141',
    cui_name: 'LUPUS ERYTHEMATOSUS, SYSTEMIC',
    accession: 'GSE88888',
    title: 'SLE kidney biopsy RNA-seq cohort',
    overall_design: '',
    year: 2019,
    platform: 'rnaseq',
    n_samples: 32,
    n_case: 16,
    n_control: 16,
    usable_for_deg: true,
    has_deg: true,
    n_genes_tested: 37663,
    n_genes_sig: 18200,
    qc_flags: 1,
  },
  {
    cui: 'C0036341',
    cui_name: 'SCHIZOPHRENIA',
    accession: 'GSE80655',
    title: 'Prefrontal cortex transcriptome in schizophrenia',
    overall_design: '',
    year: 2016,
    platform: 'rnaseq',
    n_samples: 28,
    n_case: 14,
    n_control: 14,
    usable_for_deg: true,
    has_deg: true,
    n_genes_tested: 37663,
    n_genes_sig: 5400,
    qc_flags: 1,
  },
  {
    cui: 'C0026769',
    cui_name: 'MULTIPLE SCLEROSIS',
    accession: 'GSE193268',
    title: 'RNA-seq of MS patient brain lesions vs controls',
    overall_design: '',
    year: 2022,
    platform: 'rnaseq',
    n_samples: 24,
    n_case: 12,
    n_control: 12,
    usable_for_deg: true,
    has_deg: true,
    n_genes_tested: 37663,
    n_genes_sig: 11368,
    qc_flags: 0,
  },
  {
    cui: 'C0010674',
    cui_name: 'CYSTIC FIBROSIS',
    accession: 'GSE71799',
    title: 'Airway epithelium expression profiling in cystic fibrosis',
    overall_design: '',
    year: 2017,
    platform: 'microarray',
    n_samples: 22,
    n_case: 12,
    n_control: 10,
    usable_for_deg: false,
    has_deg: false,
    n_genes_tested: 37663,
    n_genes_sig: 0,
    qc_flags: 0,
  },
];

const IBD_STUDY = MOCK_STUDIES_CORE[0];

export const MOCK_STUDIES: GeoBrowseStudy[] = [
  ...MOCK_STUDIES_CORE.map(s => ({ ...s, comparison_id: `${s.cui}__${s.accession}` })),
  {
    ...IBD_STUDY,
    comparison_id: 'C0021390__GSE165512__sex:male',
    n_samples: 94,
    n_case: 70,
    n_control: 24,
    n_genes_sig: 198,
  },
  {
    ...IBD_STUDY,
    comparison_id: 'C0021390__GSE165512__sex:female',
    n_samples: 76,
    n_case: 54,
    n_control: 22,
    n_genes_sig: 176,
  },
];

export const MOCK_GENES: GeoBrowseGene[] = [
  {
    gene_id: 2321,
    gene_symbol: 'FLT1',
    type_of_gene: 'protein-coding',
    n_diseases: 19,
    n_diseases_sig: 19,
    n_studies: 209,
    n_studies_sig: 180,
    n_deg_rows: 4500,
    n_sig_rows: 3800,
  },
  {
    gene_id: 6280,
    gene_symbol: 'S100A9',
    type_of_gene: 'protein-coding',
    n_diseases: 19,
    n_diseases_sig: 18,
    n_studies: 205,
    n_studies_sig: 175,
    n_deg_rows: 4200,
    n_sig_rows: 3600,
  },
  {
    gene_id: 3620,
    gene_symbol: 'IDO1',
    type_of_gene: 'protein-coding',
    n_diseases: 18,
    n_diseases_sig: 17,
    n_studies: 198,
    n_studies_sig: 160,
    n_deg_rows: 3900,
    n_sig_rows: 3200,
  },
  {
    gene_id: 51176,
    gene_symbol: 'LEF1',
    type_of_gene: 'protein-coding',
    n_diseases: 17,
    n_diseases_sig: 16,
    n_studies: 190,
    n_studies_sig: 155,
    n_deg_rows: 3700,
    n_sig_rows: 3000,
  },
  {
    gene_id: 4312,
    gene_symbol: 'MMP1',
    type_of_gene: 'protein-coding',
    n_diseases: 16,
    n_diseases_sig: 15,
    n_studies: 175,
    n_studies_sig: 140,
    n_deg_rows: 3500,
    n_sig_rows: 2800,
  },
  {
    gene_id: 4314,
    gene_symbol: 'MMP3',
    type_of_gene: 'protein-coding',
    n_diseases: 15,
    n_diseases_sig: 14,
    n_studies: 168,
    n_studies_sig: 130,
    n_deg_rows: 3300,
    n_sig_rows: 2600,
  },
  {
    gene_id: 1116,
    gene_symbol: 'CHI3L1',
    type_of_gene: 'protein-coding',
    n_diseases: 14,
    n_diseases_sig: 13,
    n_studies: 160,
    n_studies_sig: 125,
    n_deg_rows: 3100,
    n_sig_rows: 2400,
  },
  {
    gene_id: 3458,
    gene_symbol: 'IFNG',
    type_of_gene: 'protein-coding',
    n_diseases: 14,
    n_diseases_sig: 12,
    n_studies: 155,
    n_studies_sig: 120,
    n_deg_rows: 2900,
    n_sig_rows: 2200,
  },
  {
    gene_id: 7124,
    gene_symbol: 'TNF',
    type_of_gene: 'protein-coding',
    n_diseases: 13,
    n_diseases_sig: 12,
    n_studies: 150,
    n_studies_sig: 115,
    n_deg_rows: 2800,
    n_sig_rows: 2100,
  },
  {
    gene_id: 3569,
    gene_symbol: 'IL6',
    type_of_gene: 'protein-coding',
    n_diseases: 13,
    n_diseases_sig: 11,
    n_studies: 145,
    n_studies_sig: 110,
    n_deg_rows: 2700,
    n_sig_rows: 2000,
  },
  {
    gene_id: 1956,
    gene_symbol: 'EGFR',
    type_of_gene: 'protein-coding',
    n_diseases: 12,
    n_diseases_sig: 10,
    n_studies: 140,
    n_studies_sig: 105,
    n_deg_rows: 2600,
    n_sig_rows: 1900,
  },
  {
    gene_id: 7157,
    gene_symbol: 'TP53',
    type_of_gene: 'protein-coding',
    n_diseases: 12,
    n_diseases_sig: 10,
    n_studies: 138,
    n_studies_sig: 100,
    n_deg_rows: 2500,
    n_sig_rows: 1800,
  },
  {
    gene_id: 672,
    gene_symbol: 'BRCA1',
    type_of_gene: 'protein-coding',
    n_diseases: 11,
    n_diseases_sig: 9,
    n_studies: 130,
    n_studies_sig: 95,
    n_deg_rows: 2400,
    n_sig_rows: 1700,
  },
  {
    gene_id: 675,
    gene_symbol: 'BRCA2',
    type_of_gene: 'protein-coding',
    n_diseases: 11,
    n_diseases_sig: 9,
    n_studies: 128,
    n_studies_sig: 90,
    n_deg_rows: 2300,
    n_sig_rows: 1650,
  },
  {
    gene_id: 338,
    gene_symbol: 'APOB',
    type_of_gene: 'protein-coding',
    n_diseases: 22,
    n_diseases_sig: 20,
    n_studies: 180,
    n_studies_sig: 28,
    n_deg_rows: 2100,
    n_sig_rows: 1680,
  },
  {
    gene_id: 3949,
    gene_symbol: 'LDLR',
    type_of_gene: 'protein-coding',
    n_diseases: 14,
    n_diseases_sig: 12,
    n_studies: 95,
    n_studies_sig: 12,
    n_deg_rows: 980,
    n_sig_rows: 720,
  },
];

/** Genes flagged as recurrently perturbed at build time (independent of live browse thresholds) */
export const MOCK_RECURRENT_GENES: GeoBrowseRecurrentGene[] = [
  { gene_id: 338, gene_symbol: 'APOB', n_studies: 28 },
  { gene_id: 3949, gene_symbol: 'LDLR', n_studies: 12 },
  { gene_id: 2321, gene_symbol: 'FLT1', n_studies: 18 },
  { gene_id: 6280, gene_symbol: 'S100A9', n_studies: 16 },
  { gene_id: 4314, gene_symbol: 'MMP3', n_studies: 14 },
  { gene_id: 4312, gene_symbol: 'MMP1', n_studies: 13 },
  { gene_id: 1116, gene_symbol: 'CHI3L1', n_studies: 11 },
];

const GENE_STUDY_TEMPLATES: Pick<
  GeoBrowseGeneStudy,
  'cui' | 'cui_name' | 'accession' | 'comparison_id' | 'title' | 'year'
>[] = [
  ...MOCK_STUDIES.map(s => ({
    cui: s.cui,
    cui_name: s.cui_name,
    accession: s.accession,
    comparison_id: s.comparison_id,
    title: s.title,
    year: s.year,
  })),
  {
    cui: 'C0002736',
    cui_name: 'AMYOTROPHIC LATERAL SCLEROSIS',
    accession: 'GSE98289',
    comparison_id: 'C0002736__GSE98289',
    title: 'Motor cortex RNA-seq in ALS patients',
    year: 2019,
  },
  {
    cui: 'C0033860',
    cui_name: 'PSORIASIS',
    accession: 'GSE67785',
    comparison_id: 'C0033860__GSE67785',
    title: 'Psoriasis lesional skin RNA-seq cohort',
    year: 2016,
  },
  {
    cui: 'C0020538',
    cui_name: 'HYPERTENSION',
    accession: 'GSE120774',
    comparison_id: 'C0020538__GSE120774',
    title: 'Vascular tissue expression in hypertension',
    year: 2018,
  },
  {
    cui: 'C0018801',
    cui_name: 'HEART FAILURE',
    accession: 'GSE141910',
    comparison_id: 'C0018801__GSE141910',
    title: 'Left ventricle transcriptome in heart failure',
    year: 2020,
  },
  {
    cui: 'C0027051',
    cui_name: 'OBESITY',
    accession: 'GSE59034',
    comparison_id: 'C0027051__GSE59034',
    title: 'Adipose tissue RNA-seq in obesity',
    year: 2014,
  },
  {
    cui: 'C0011570',
    cui_name: 'DEPRESSION',
    accession: 'GSE101521',
    comparison_id: 'C0011570__GSE101521',
    title: 'Prefrontal cortex in major depression',
    year: 2017,
  },
  {
    cui: 'C0038454',
    cui_name: 'STROKE',
    accession: 'GSE56267',
    comparison_id: 'C0038454__GSE56267',
    title: 'Blood transcriptome after ischemic stroke',
    year: 2014,
  },
  {
    cui: 'C0011860',
    cui_name: 'DIABETES MELLITUS, TYPE 2',
    accession: 'GSE76892',
    comparison_id: 'C0011860__GSE76892',
    title: 'Skeletal muscle RNA-seq in type 2 diabetes',
    year: 2016,
  },
  {
    cui: 'C0024117',
    cui_name: 'CHRONIC OBSTRUCTIVE PULMONARY DISEASE',
    accession: 'GSE38974',
    comparison_id: 'C0024117__GSE38974',
    title: 'Lung tissue expression in COPD',
    year: 2012,
  },
  {
    cui: 'C0019209',
    cui_name: 'HEPATOCELLULAR CARCINOMA',
    accession: 'GSE14520',
    comparison_id: 'C0019209__GSE14520',
    title: 'Liver tumour RNA-seq cohort',
    year: 2010,
  },
  {
    cui: 'C0006142',
    cui_name: 'BREAST NEOPLASMS',
    accession: 'GSE42568',
    comparison_id: 'C0006142__GSE42568',
    title: 'Breast cancer tumour transcriptome',
    year: 2013,
  },
  {
    cui: 'C0153314',
    cui_name: 'CORONARY ARTERY DISEASE',
    accession: 'GSE40275',
    comparison_id: 'C0153314__GSE40275',
    title: 'Coronary artery plaque RNA-seq',
    year: 2012,
  },
  {
    cui: 'C0037286',
    cui_name: 'SKIN NEOPLASMS',
    accession: 'GSE65904',
    comparison_id: 'C0037286__GSE65904',
    title: 'Melanoma skin biopsy transcriptome',
    year: 2015,
  },
  {
    cui: 'C0037315',
    cui_name: 'SLEEP APNEA SYNDROMES',
    accession: 'GSE135917',
    comparison_id: 'C0037315__GSE135917',
    title: 'Adipose tissue in obstructive sleep apnea',
    year: 2019,
  },
  {
    cui: 'C0024198',
    cui_name: 'LYME DISEASE',
    accession: 'GSE84244',
    comparison_id: 'C0024198__GSE84244',
    title: 'Whole blood expression in Lyme disease',
    year: 2016,
  },
  {
    cui: 'C0036572',
    cui_name: 'SEPSIS',
    accession: 'GSE65682',
    comparison_id: 'C0036572__GSE65682',
    title: 'Whole blood transcriptome in sepsis',
    year: 2014,
  },
  {
    cui: 'C0035222',
    cui_name: 'RESPIRATORY DISTRESS SYNDROME',
    accession: 'GSE104148',
    comparison_id: 'C0035222__GSE104148',
    title: 'Lung injury RNA-seq in ARDS',
    year: 2018,
  },
  {
    cui: 'C0029456',
    cui_name: 'OSTEOPOROSIS',
    accession: 'GSE56814',
    comparison_id: 'C0029456__GSE56814',
    title: 'Bone marrow expression in osteoporosis',
    year: 2014,
  },
  {
    cui: 'C0032460',
    cui_name: 'POLYCYSTIC OVARY SYNDROME',
    accession: 'GSE80432',
    comparison_id: 'C0032460__GSE80432',
    title: 'Granulosa cell transcriptome in PCOS',
    year: 2016,
  },
  {
    cui: 'C0011581',
    cui_name: 'DEPRESSIVE DISORDER, MAJOR',
    accession: 'GSE98793',
    comparison_id: 'C0011581__GSE98793',
    title: 'Hippocampus RNA-seq in major depression',
    year: 2017,
  },
];

const STUDY_DETAIL_GSE165512: GeoBrowseStudyDetail = {
  cui: 'C0021390',
  cui_name: 'INFLAMMATORY BOWEL DISEASES',
  accession: 'GSE165512',
  comparison_id: 'C0021390__GSE165512',
  title: 'Characterization of the IL23-IL17 immune axis in IBD patients',
  summary: 'Bulk RNA-seq of colon biopsies from IBD patients and healthy controls.',
  overall_design: 'Mucosal biopsy cohort',
  year: 2021,
  pubmedids: '34567890',
  platform: 'rnaseq',
  n_samples: 170,
  n_case: 124,
  n_control: 46,
  usable_for_deg: true,
  has_deg: true,
  n_genes_tested: 37663,
  n_genes_sig: 240,
  qc_flags: 2,
  control_caveat:
    'Healthy control samples are suitable for comparison; tissue covariate included in model.',
  qc_flag_rate: 3,
  has_gsea_curated: true,
  has_gsea_all: true,
  has_gsea_reactome: true,
  has_gsea_wikipathways: false,
  covariates_in_model: 'tissue',
  confident_sex_mismatches: 0,
  confident_group_consistency_flags: 2,
};

function toStudyDetail(study: GeoBrowseStudy): GeoBrowseStudyDetail {
  return {
    ...study,
    summary: study.title,
    pubmedids: '',
    platform: study.platform,
    control_caveat: '',
    qc_flag_rate: study.qc_flags > 0 ? 3 : 0,
    has_gsea_curated: true,
    has_gsea_all: false,
    has_gsea_reactome: true,
    has_gsea_wikipathways: false,
    covariates_in_model: study.overall_design,
    confident_sex_mismatches: 0,
    confident_group_consistency_flags: study.qc_flags,
  };
}

export function getMockStudyDetail(
  cui: string,
  accession: string,
  comparisonId?: string,
): GeoBrowseStudyDetail | null {
  if (comparisonId) {
    const byComparison = MOCK_STUDIES.find(s => s.comparison_id === comparisonId);
    if (byComparison) return toStudyDetail(byComparison);

    const templateByComparison = GENE_STUDY_TEMPLATES.find(t => t.comparison_id === comparisonId);
    if (templateByComparison) {
      return toStudyDetail({
        ...templateByComparison,
        overall_design: '',
        platform: 'rnaseq',
        n_samples: 40,
        n_case: 24,
        n_control: 16,
        usable_for_deg: true,
        has_deg: true,
        n_genes_tested: 37663,
        n_genes_sig: 1200,
        qc_flags: 0,
      });
    }
  }

  if (!comparisonId && cui === 'C0021390' && accession === 'GSE165512') {
    return STUDY_DETAIL_GSE165512;
  }

  const study =
    MOCK_STUDIES.find(
      s =>
        s.cui === cui &&
        s.accession === accession &&
        s.comparison_id === `${cui}__${accession}`,
    ) ?? MOCK_STUDIES.find(s => s.cui === cui && s.accession === accession);
  if (study) return toStudyDetail(study);

  // Gene Details forest/table can reference studies beyond the browse index
  const template = GENE_STUDY_TEMPLATES.find(t => t.cui === cui && t.accession === accession);
  if (!template) return null;

  return toStudyDetail({
    ...template,
    overall_design: '',
    platform: 'rnaseq',
    n_samples: 40,
    n_case: 24,
    n_control: 16,
    usable_for_deg: true,
    has_deg: true,
    n_genes_tested: 37663,
    n_genes_sig: 1200,
    qc_flags: 0,
  });
}

export const MOCK_SAMPLES_GSE165512: GeoBrowseSample[] = Array.from({ length: 24 }, (_, i) => ({
  sample_id: `GSE165512_S${i + 1}`,
  group: i < 16 ? 'case' : 'control',
  sex: i % 3 === 0 ? 'male' : i % 3 === 1 ? 'female' : 'unknown',
  sex_source: i % 3 === 2 ? 'not_reported' : 'reported',
  tissue: i % 2 === 0 ? 'colon' : 'ileum',
}));

export function getMockSamples(cui: string, accession: string): GeoBrowseSample[] {
  if (cui === 'C0021390' && accession === 'GSE165512') {
    return MOCK_SAMPLES_GSE165512;
  }
  return [
    { sample_id: `${accession}_S1`, group: 'case', sex: 'unknown', sex_source: 'not_reported' },
    { sample_id: `${accession}_S2`, group: 'control', sex: 'unknown', sex_source: 'not_reported' },
  ];
}

const VOLCANO_GENES = [
  { gene_id: 4314, gene_symbol: 'MMP3', log2FoldChange: 4.2, padj: 1e-20 },
  { gene_id: 4312, gene_symbol: 'MMP1', log2FoldChange: 3.8, padj: 1e-18 },
  { gene_id: 1116, gene_symbol: 'CHI3L1', log2FoldChange: 3.5, padj: 1e-16 },
  { gene_id: 3502, gene_symbol: 'IGHG4', log2FoldChange: 3.2, padj: 1e-14 },
  { gene_id: 3501, gene_symbol: 'IGHG3', log2FoldChange: 2.9, padj: 1e-12 },
  { gene_id: 102724621, gene_symbol: 'LOC102724621', log2FoldChange: -4.5, padj: 1e-15 },
];

function seededRandom(seed: number): number {
  const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
}

function generateVolcanoPoints(thresholds: GeoBrowseThresholds, total = 200): GeoBrowseVolcanoPoint[] {
  const points: GeoBrowseVolcanoPoint[] = [];

  for (const g of VOLCANO_GENES) {
    points.push({
      ...g,
      significant: g.padj < thresholds.padj && Math.abs(g.log2FoldChange) >= thresholds.fc,
    });
  }

  const backgroundCount = total - VOLCANO_GENES.length;
  for (let i = 0; i < backgroundCount; i += 1) {
    const r1 = seededRandom(i);
    const r2 = seededRandom(i + 500);
    const r3 = seededRandom(i + 1000);

    // ~88% background cloud (centre-bottom); ~12% wing candidates
    const inWings = r1 < 0.12;
    let log2fc: number;
    let padj: number;

    if (inWings) {
      const direction = r2 > 0.5 ? 1 : -1;
      log2fc = direction * (thresholds.fc + r3 * 4);
      padj = Math.pow(10, -(1.2 + r3 * 6));
    } else {
      log2fc = (r2 - 0.5) * 0.6;
      padj = 0.08 + r3 * 0.92;
    }

    const gene_id = 10000 + i;
    points.push({
      gene_id,
      gene_symbol: `GENE${i}`,
      log2FoldChange: log2fc,
      padj,
      significant: padj < thresholds.padj && Math.abs(log2fc) >= thresholds.fc,
    });
  }

  return points;
}

export function getMockVolcano(
  cui: string,
  accession: string,
  thresholds: GeoBrowseThresholds,
  comparisonId?: string,
): GeoBrowseVolcanoPoint[] {
  const seed = comparisonId ? comparisonId.length * 17 : 328;
  if (cui === 'C0021390' && accession === 'GSE165512') {
    return generateVolcanoPoints(thresholds, seed);
  }
  return generateVolcanoPoints(thresholds, 80);
}

export function getMockPca(cui: string, accession: string): GeoBrowsePcaPoint[] {
  const pct_var_pc1 = 32.5;
  const pct_var_pc2 = 18.2;
  const samples = getMockSamples(cui, accession);
  return samples.map((s, i) => {
    const r1 = seededRandom(i + 200);
    const r2 = seededRandom(i + 400);
    const isCase = s.group === 'case';
    return {
      sample_id: s.sample_id,
      group: s.group,
      pc1: (isCase ? 2.5 : -2.5) + (r1 - 0.5) * 2,
      pc2: (isCase ? 1 : -1) + (r2 - 0.5) * 2,
      pct_var_pc1,
      pct_var_pc2,
    };
  });
}

export function getMockTopDegs(
  cui: string,
  accession: string,
  thresholds: GeoBrowseThresholds,
  comparisonId?: string,
): GeoBrowseDegRow[] {
  const volcano = getMockVolcano(cui, accession, thresholds, comparisonId);
  return volcano
    .filter(p => p.significant)
    .sort((a, b) => a.padj - b.padj)
    .slice(0, 50)
    .map(p => ({
      gene_id: p.gene_id,
      gene_symbol: p.gene_symbol,
      comparison_id: comparisonId,
      baseMean: 400 + seededRandom(p.gene_id) * 8000,
      log2FoldChange: p.log2FoldChange,
      lfcSE: 0.1 + seededRandom(p.gene_id + 1) * 0.3,
      stat: p.log2FoldChange / 0.2,
      pvalue: p.padj * 0.5,
      padj: p.padj,
    }));
}

export const MOCK_ENRICHMENT_CURATED: GeoBrowseEnrichmentRow[] = [
  {
    gene_set_id: 'C0021390',
    gene_set_name: 'INFLAMMATORY BOWEL DISEASES',
    collection: 'CURATED',
    es: 0.65,
    nes: 2.82,
    nom_pval: 0.001,
    fdr_qval: 0.0,
    tag_pct: 0.47,
    gene_pct: 0.31,
    lead_genes: 'MMP3,MMP1,CHI3L1',
  },
  {
    gene_set_id: 'C0024141',
    gene_set_name: 'LUPUS ERYTHEMATOSUS, SYSTEMIC',
    collection: 'CURATED',
    es: 0.55,
    nes: 2.45,
    nom_pval: 0.002,
    fdr_qval: 0.01,
    tag_pct: 0.42,
    gene_pct: 0.28,
    lead_genes: 'S100A9,FLT1',
  },
  {
    gene_set_id: 'C0033860',
    gene_set_name: 'PSORIASIS',
    collection: 'CURATED',
    es: 0.48,
    nes: 2.1,
    nom_pval: 0.004,
    fdr_qval: 0.02,
    tag_pct: 0.38,
    gene_pct: 0.24,
    lead_genes: 'IDO1,LEF1',
  },
  {
    gene_set_id: 'C0011847',
    gene_set_name: 'DIABETES MELLITUS, TYPE 2',
    collection: 'CURATED',
    es: 0.44,
    nes: 1.92,
    nom_pval: 0.006,
    fdr_qval: 0.03,
    tag_pct: 0.35,
    gene_pct: 0.22,
    lead_genes: 'TNF,IL6',
  },
  {
    gene_set_id: 'C0002395',
    gene_set_name: "ALZHEIMER'S DISEASE",
    collection: 'CURATED',
    es: 0.41,
    nes: 1.75,
    nom_pval: 0.008,
    fdr_qval: 0.04,
    tag_pct: 0.32,
    gene_pct: 0.2,
    lead_genes: 'APOE,TREM2',
  },
  {
    gene_set_id: 'C0026769',
    gene_set_name: 'MULTIPLE SCLEROSIS',
    collection: 'CURATED',
    es: 0.39,
    nes: 1.58,
    nom_pval: 0.01,
    fdr_qval: 0.05,
    tag_pct: 0.29,
    gene_pct: 0.19,
    lead_genes: 'IFNG,CD8A',
  },
  {
    gene_set_id: 'C0003873',
    gene_set_name: 'RHEUMATOID ARTHRITIS',
    collection: 'CURATED',
    es: 0.36,
    nes: 1.42,
    nom_pval: 0.012,
    fdr_qval: 0.06,
    tag_pct: 0.27,
    gene_pct: 0.18,
    lead_genes: 'MMP1,MMP3',
  },
  {
    gene_set_id: 'C0011581',
    gene_set_name: 'DEPRESSIVE DISORDER, MAJOR',
    collection: 'CURATED',
    es: 0.33,
    nes: 1.28,
    nom_pval: 0.015,
    fdr_qval: 0.08,
    tag_pct: 0.25,
    gene_pct: 0.16,
    lead_genes: 'SLC6A4,HTR2A',
  },
  {
    gene_set_id: 'C0020538',
    gene_set_name: 'HYPERTENSION',
    collection: 'CURATED',
    es: 0.31,
    nes: 1.15,
    nom_pval: 0.018,
    fdr_qval: 0.1,
    tag_pct: 0.23,
    gene_pct: 0.15,
    lead_genes: 'AGT,ACE',
  },
  {
    gene_set_id: 'C0018802',
    gene_set_name: 'HEART FAILURE',
    collection: 'CURATED',
    es: 0.28,
    nes: 1.05,
    nom_pval: 0.02,
    fdr_qval: 0.12,
    tag_pct: 0.21,
    gene_pct: 0.14,
    lead_genes: 'MYH7,TNNT2',
  },
  {
    gene_set_id: 'C4721453',
    gene_set_name: 'NEURODEVELOPMENTAL DISORDER WITH POOR GROWTH AND DISTINCTIVE FACIES',
    collection: 'CURATED',
    es: -0.62,
    nes: -2.65,
    nom_pval: 0.001,
    fdr_qval: 0.0,
    tag_pct: 0.44,
    gene_pct: 0.29,
    lead_genes: 'KMT2D,DDX3X',
  },
  {
    gene_set_id: 'C3151526',
    gene_set_name: 'NEURODEVELOPMENTAL DISORDER WITH HYPOTONIA AND VARIABLE INTELLECTUAL DISABILITY',
    collection: 'CURATED',
    es: -0.58,
    nes: -2.35,
    nom_pval: 0.002,
    fdr_qval: 0.01,
    tag_pct: 0.4,
    gene_pct: 0.26,
    lead_genes: 'SYNGAP1,SCN2A',
  },
  {
    gene_set_id: 'C0029456',
    gene_set_name: 'OSTEOPOROSIS',
    collection: 'CURATED',
    es: -0.52,
    nes: -2.08,
    nom_pval: 0.004,
    fdr_qval: 0.02,
    tag_pct: 0.36,
    gene_pct: 0.23,
    lead_genes: 'COL1A1,LRP5',
  },
  {
    gene_set_id: 'C0036341',
    gene_set_name: 'SCHIZOPHRENIA',
    collection: 'CURATED',
    es: -0.48,
    nes: -1.88,
    nom_pval: 0.006,
    fdr_qval: 0.03,
    tag_pct: 0.33,
    gene_pct: 0.21,
    lead_genes: 'COMT,DRD2',
  },
  {
    gene_set_id: 'C0010674',
    gene_set_name: 'CYSTIC FIBROSIS',
    collection: 'CURATED',
    es: -0.45,
    nes: -1.72,
    nom_pval: 0.008,
    fdr_qval: 0.04,
    tag_pct: 0.3,
    gene_pct: 0.19,
    lead_genes: 'CFTR,SLC26A9',
  },
  {
    gene_set_id: 'C0024117',
    gene_set_name: 'CHRONIC OBSTRUCTIVE PULMONARY DISEASE',
    collection: 'CURATED',
    es: -0.42,
    nes: -1.55,
    nom_pval: 0.01,
    fdr_qval: 0.05,
    tag_pct: 0.28,
    gene_pct: 0.18,
    lead_genes: 'SERPINA1,CHRNA3',
  },
  {
    gene_set_id: 'C0010054',
    gene_set_name: 'CORONARY ARTERY DISEASE',
    collection: 'CURATED',
    es: -0.38,
    nes: -1.38,
    nom_pval: 0.012,
    fdr_qval: 0.06,
    tag_pct: 0.26,
    gene_pct: 0.17,
    lead_genes: 'LDLR,APOB',
  },
  {
    gene_set_id: 'C0023891',
    gene_set_name: 'LIVER CIRRHOSIS',
    collection: 'CURATED',
    es: -0.35,
    nes: -1.22,
    nom_pval: 0.015,
    fdr_qval: 0.08,
    tag_pct: 0.24,
    gene_pct: 0.15,
    lead_genes: 'ALB,TGFB1',
  },
  {
    gene_set_id: 'C0006142',
    gene_set_name: 'BREAST NEOPLASMS',
    collection: 'CURATED',
    es: -0.32,
    nes: -1.08,
    nom_pval: 0.018,
    fdr_qval: 0.1,
    tag_pct: 0.22,
    gene_pct: 0.14,
    lead_genes: 'BRCA1,BRCA2',
  },
  {
    gene_set_id: 'C0007131',
    gene_set_name: 'RENAL CELL CARCINOMA',
    collection: 'CURATED',
    es: -0.28,
    nes: -0.95,
    nom_pval: 0.02,
    fdr_qval: 0.12,
    tag_pct: 0.2,
    gene_pct: 0.13,
    lead_genes: 'VHL,PBRM1',
  },
];

export const MOCK_ENRICHMENT_REACTOME: GeoBrowseEnrichmentRow[] = [
  {
    gene_set_id: 'R-HSA-1474244',
    gene_set_name: 'Extracellular matrix organization',
    collection: 'REACTOME',
    es: 0.72,
    nes: 2.4,
    nom_pval: 0.0005,
    fdr_qval: 0.01,
    tag_pct: 0.4,
    gene_pct: 0.32,
    lead_genes: 'MMP3,MMP1',
  },
  {
    gene_set_id: 'R-HSA-1280215',
    gene_set_name: 'Cytokine signaling in immune system',
    collection: 'REACTOME',
    es: 0.58,
    nes: 1.9,
    nom_pval: 0.003,
    fdr_qval: 0.05,
    tag_pct: 0.3,
    gene_pct: 0.25,
    lead_genes: 'IFNG,TNF,IL6',
  },
  {
    gene_set_id: 'R-HSA-168256',
    gene_set_name: 'Immune system',
    collection: 'REACTOME',
    es: 0.52,
    nes: 1.65,
    nom_pval: 0.005,
    fdr_qval: 0.07,
    tag_pct: 0.28,
    gene_pct: 0.22,
    lead_genes: 'CD3D,CD8A',
  },
  {
    gene_set_id: 'R-HSA-9006934',
    gene_set_name: 'Signaling by receptor tyrosine kinases',
    collection: 'REACTOME',
    es: -0.55,
    nes: -2.1,
    nom_pval: 0.002,
    fdr_qval: 0.02,
    tag_pct: 0.35,
    gene_pct: 0.24,
    lead_genes: 'EGFR,ERBB2',
  },
  {
    gene_set_id: 'R-HSA-71291',
    gene_set_name: 'Metabolism of amino acids and derivatives',
    collection: 'REACTOME',
    es: -0.48,
    nes: -1.75,
    nom_pval: 0.004,
    fdr_qval: 0.04,
    tag_pct: 0.31,
    gene_pct: 0.2,
    lead_genes: 'ASS1,GLUD1',
  },
];

export function getMockEnrichment(
  collection: 'CURATED' | 'ALL' | 'REACTOME' | 'WIKIPATHWAYS',
  fdr: number,
  top_n: number,
): GeoBrowseEnrichmentRow[] {
  const rows =
    collection === 'REACTOME' || collection === 'WIKIPATHWAYS'
      ? MOCK_ENRICHMENT_REACTOME
      : MOCK_ENRICHMENT_CURATED;

  const filtered = rows.filter(r => r.fdr_qval <= fdr);
  const enriched = filtered
    .filter(r => r.nes > 0)
    .sort((a, b) => b.nes - a.nes)
    .slice(0, top_n);
  const depleted = filtered
    .filter(r => r.nes < 0)
    .sort((a, b) => a.nes - b.nes)
    .slice(0, top_n);

  return [...enriched, ...depleted];
}

export function getMockRecurrentGenes(): GeoBrowseRecurrentGene[] {
  return MOCK_RECURRENT_GENES;
}

const BUILD_TIME_THRESHOLDS: GeoBrowseThresholds = { padj: 0.05, fc: 1 };

function hashSymbol(symbol: string): number {
  return symbol.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
}

function buildMockGeneStudyRow(
  geneSymbol: string,
  template: (typeof GENE_STUDY_TEMPLATES)[number],
  index: number,
): GeoBrowseGeneStudy {
  const seed = hashSymbol(geneSymbol) * 1000 + index;
  const r1 = seededRandom(seed);
  const r2 = seededRandom(seed + 17);
  const r3 = seededRandom(seed + 41);
  const r4 = seededRandom(seed + 73);

  const log2FoldChange = (r1 * 2 - 1) * (1.2 + r2 * 3.2);
  const lfcSE = 0.12 + r3 * 0.55;
  const padj = Math.pow(10, -(1 + r4 * 6));
  const significant =
    padj < BUILD_TIME_THRESHOLDS.padj &&
    Math.abs(log2FoldChange) >= BUILD_TIME_THRESHOLDS.fc;

  return {
    ...template,
    log2FoldChange: Number(log2FoldChange.toFixed(3)),
    lfcSE: Number(lfcSE.toFixed(3)),
    padj,
    baseMean: Math.round(180 + r2 * 1200),
    significant,
  };
}

export function getMockGeneStudySummary(geneSymbol: string): GeoBrowseGeneStudy[] {
  const recurrent = MOCK_RECURRENT_GENES.find(g => g.gene_symbol === geneSymbol);
  const studyCount = recurrent?.n_studies ?? 6;
  const rows: GeoBrowseGeneStudy[] = [];

  for (let i = 0; i < studyCount; i += 1) {
    const template = GENE_STUDY_TEMPLATES[i % GENE_STUDY_TEMPLATES.length];
    rows.push(buildMockGeneStudyRow(geneSymbol, template, i));
  }

  return rows.sort((a, b) => a.log2FoldChange - b.log2FoldChange);
}

export function getMockGeneStudies(geneSymbol: string, thresholds: GeoBrowseThresholds): GeoBrowseGeneStudy[] {
  if (geneSymbol === 'FLT1') {
    return [
      {
        cui: 'C0024141',
        cui_name: 'LUPUS ERYTHEMATOSUS, SYSTEMIC',
        accession: 'GSE110999',
        comparison_id: 'C0024141__GSE110999',
        title: 'Transcriptomic profiling of systemic lupus erythematosus peripheral blood',
        year: 2018,
        log2FoldChange: 3.711,
        lfcSE: 0.412,
        padj: 0.00012,
        baseMean: 842,
        significant: true,
      },
      {
        cui: 'C0033860',
        cui_name: 'PSORIASIS',
        accession: 'GSE74697',
        comparison_id: 'C0033860__GSE74697',
        title: 'Psoriasis skin biopsy transcriptome analysis',
        year: 2015,
        log2FoldChange: 2.104,
        lfcSE: 0.358,
        padj: 0.0034,
        baseMean: 615,
        significant: true,
      },
      {
        cui: 'C0026769',
        cui_name: 'MULTIPLE SCLEROSIS',
        accession: 'GSE193268',
        comparison_id: 'C0026769__GSE193268',
        title: 'RNA-seq of MS patient brain lesions vs controls',
        year: 2022,
        log2FoldChange: -2.438,
        lfcSE: 0.521,
        padj: 0.0089,
        baseMean: 478,
        significant: true,
      },
      {
        cui: 'C0021390',
        cui_name: 'INFLAMMATORY BOWEL DISEASES',
        accession: 'GSE165512',
        comparison_id: 'C0021390__GSE165512',
        title: 'Characterization of the IL23-IL17 immune axis in IBD patients',
        year: 2021,
        log2FoldChange: 1.892,
        lfcSE: 0.289,
        padj: 0.014,
        baseMean: 392,
        significant: true,
      },
      {
        cui: 'C0021390',
        cui_name: 'INFLAMMATORY BOWEL DISEASES',
        accession: 'GSE165512',
        comparison_id: 'C0021390__GSE165512__sex:male',
        title: 'Characterization of the IL23-IL17 immune axis in IBD patients',
        year: 2021,
        log2FoldChange: 2.241,
        lfcSE: 0.331,
        padj: 0.009,
        baseMean: 410,
        significant: true,
      },
      {
        cui: 'C0021390',
        cui_name: 'INFLAMMATORY BOWEL DISEASES',
        accession: 'GSE165512',
        comparison_id: 'C0021390__GSE165512__sex:female',
        title: 'Characterization of the IL23-IL17 immune axis in IBD patients',
        year: 2021,
        log2FoldChange: 1.455,
        lfcSE: 0.274,
        padj: 0.021,
        baseMean: 360,
        significant: true,
      },
    ].filter(r => r.padj < thresholds.padj && Math.abs(r.log2FoldChange) >= thresholds.fc);
  }

  const gene = MOCK_GENES.find(g => g.gene_symbol === geneSymbol);
  if (!gene) return [];

  return getMockGeneStudySummary(geneSymbol).filter(
    r => r.padj < thresholds.padj && Math.abs(r.log2FoldChange) >= thresholds.fc,
  );
}

export function filterStudiesByCui(cui?: string): GeoBrowseStudy[] {
  if (!cui) return MOCK_STUDIES;
  return MOCK_STUDIES.filter(s => s.cui === cui);
}

export function sortGenes(
  genes: GeoBrowseGene[],
  sort: string,
): GeoBrowseGene[] {
  const copy = [...genes];
  copy.sort((a, b) => {
    switch (sort) {
      case 'gene_symbol':
        return a.gene_symbol.localeCompare(b.gene_symbol);
      case 'n_diseases':
        return b.n_diseases - a.n_diseases;
      case 'n_studies':
        return b.n_studies - a.n_studies;
      case 'n_studies_sig':
        return b.n_studies_sig - a.n_studies_sig;
      case 'n_diseases_sig':
      default:
        return b.n_diseases_sig - a.n_diseases_sig;
    }
  });
  return copy;
}
